import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import './NotificationDropdown.css';

const NotificationDropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications,
    getTypeIcon,
    getTypeColor,
    formatDate
  } = useNotifications();

  useEffect(() => {
    // Fetch notifications when dropdown opens only if user data is available
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData?.Farm?.id) {
      fetchNotifications({ status: 'active' });
    }
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    // Mark as read
    await markAsRead(notification.id);

    // Navigate based on type
    switch(notification.type) {
      case 'tasks':
        navigate('/tasks');
        break;
      case 'problems':
        navigate('/problems');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      default:
        navigate('/notifications');
    }

    onClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    onClose();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  const recentNotifications = notifications
    .filter(n => n.status === 'active')
    .slice(0, 5);

  return (
    <div className="notification-dropdown">
      <div className="notification-dropdown-header">
        <h6 className="mb-0">{t('notifications.title')}</h6>
        {recentNotifications.length > 0 && (
          <button
            className="mark-all-btn"
            onClick={handleMarkAllAsRead}
            title={t('notifications.markAllAsRead')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 11 12 14 22 4"></polyline>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
            </svg>
          </button>
        )}
      </div>

      <div className="notification-dropdown-body">
        {loading ? (
          <div className="notification-loading">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : recentNotifications.length > 0 ? (
          <div className="notification-list">
            {recentNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item ${notification.status === 'active' ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div
                  className="notification-icon"
                  style={{ backgroundColor: getTypeColor(notification.type) + '20', color: getTypeColor(notification.type) }}
                >
                  {getTypeIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-title">{notification.title}</div>
                  <div className="notification-description">{notification.description}</div>
                  <div className="notification-time">{formatDate(notification.reportedDate)}</div>
                </div>
                <button
                  className="notification-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                  title={t('notifications.delete')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="notification-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p>{t('notifications.noNotifications')}</p>
          </div>
        )}
      </div>

      <div className="notification-dropdown-footer">
        <button className="view-all-btn" onClick={handleViewAll}>
          {t('notifications.viewAll')}
        </button>
      </div>
    </div>
  );
};

export default NotificationDropdown;